import { isAxiosError } from "axios";
import i18n from "i18next";

import { API_UNHANDLED_ERROR_MESSAGE, ERRORS } from "consts";

const readMessage = (value: unknown): string | undefined => {
  if (typeof value === "string" && value) return value;
  if (value && typeof value === "object" && typeof (value as { message?: unknown }).message === "string")
    return (value as { message: string }).message || undefined;
  return undefined;
};

/** Admin screens only: the API's raw English reason, never translated; use `getUserFacingApiErrorMessage` for users. */
export const getApiErrorMessage = (error: unknown): string => {
  if (isAxiosError(error)) {
    const body = error.response?.data as { message?: unknown; data?: unknown } | undefined;
    const reason = readMessage(body?.data) ?? (body?.message !== API_UNHANDLED_ERROR_MESSAGE ? readMessage(body?.message) : undefined);
    if (reason) return reason;
  }
  return error instanceof Error ? error.message : "Unknown error";
};

/** The stable reason code the API sends when it throws an error from one of its error catalogs. */
export const getApiErrorCode = (error: unknown): string | undefined => {
  if (!isAxiosError(error)) return undefined;
  const errorCode = (error.response?.data as { errorCode?: unknown } | undefined)?.errorCode;
  // Catalog codes are UPPER_SNAKE; anything else (dots, objects) must not become a translation path.
  return typeof errorCode === "string" && /^[A-Z0-9_]+$/.test(errorCode) ? errorCode : undefined;
};

/** Values the API sent for a coded message (e.g. hectares); only strings and numbers are kept. */
export const getApiErrorParams = (error: unknown): Record<string, string | number> | undefined => {
  if (!isAxiosError(error)) return undefined;
  const errorParams = (error.response?.data as { errorParams?: unknown } | undefined)?.errorParams;
  if (!errorParams || typeof errorParams !== "object" || Array.isArray(errorParams)) return undefined;
  return Object.fromEntries(Object.entries(errorParams).filter(([, value]) => typeof value === "string" || typeof value === "number"));
};

// Numbers in a message follow the user's language (e.g. 100,5 in German), so the API sends them raw.
const formatErrorParams = (errorParams?: Record<string, string | number>) =>
  errorParams &&
  Object.fromEntries(
    Object.entries(errorParams).map(([name, value]) => [
      name,
      typeof value === "number" ? new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 }).format(value) : value,
    ])
  );

/** A request that ran out of time, or got no answer while the device is online (e.g. a gateway timeout the browser blocked). */
export const isRequestTimeoutError = (error: unknown): boolean => {
  if (!isAxiosError(error) || error.code === "ERR_CANCELED") return false;
  if (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT") return true;
  if (error.response) return error.response.status === 504;
  return typeof navigator === "undefined" || navigator.onLine;
};

/** Translated text to show a user for a failed request (or none): its error code's translation when one exists, else the timeout text, else `fallback`, else the status-based message. */
export const getUserFacingApiErrorMessage = (error: unknown, fallback?: string): string => {
  const errorCode = getApiErrorCode(error);
  if (errorCode && i18n.exists(`errors.codes.${errorCode}`, { ns: "global" }))
    return i18n.t(`errors.codes.${errorCode}`, { ns: "global", replace: formatErrorParams(getApiErrorParams(error)) });
  if (isRequestTimeoutError(error)) return i18n.t("errors.timeout");
  if (fallback) return fallback;

  const status = isAxiosError(error) ? error.response?.status : undefined;
  return i18n.t((status && ERRORS[status]) || "errors.unknown");
};
